import { useQuery } from '@tanstack/react-query';

import { canAddArtifact } from '@/features/artifacts/artifact-display.js';
import {
  knowledgeSourcesQueryOptions,
  type KnowledgeSource,
} from '@/lib/api/workspace-knowledge.js';

/**
 * Personal OneDrive as a source of workspace knowledge — the rules both ways in share, kept out of the
 * components so one implementation decides them and one test covers them.
 *
 * A folder registered out of somebody's own drive answers only the members who may open its files in
 * SharePoint themselves: the search checks the asking member's own access. Colleagues without that
 * access see nothing of it until the owner shares the folder in SharePoint or uploads the files to the
 * workspace.
 */

/** The two registry kinds an item of a personal drive is registered under. */
const ONEDRIVE_SOURCE_TYPES = new Set(['onedrive_folder', 'onedrive_file']);

/** Whether a listed source came out of somebody's personal OneDrive. `type` is an OPEN union on the wire. */
export function isOneDriveKnowledgeSource(source: { type: string }): boolean {
  return ONEDRIVE_SOURCE_TYPES.has(source.type);
}

/**
 * Where a OneDrive item lives, as the platform addresses it: the folder its owner granted this workspace,
 * plus the item inside it. A Graph `item_id` is unique only within its own drive, and the scope names both
 * the drive and the connection that reaches it — so the pair is the address, and the item id alone is not.
 */
export interface OneDriveKnowledgeAddress {
  scopeId: string;
  itemId: string;
}

/** The workspace's knowledge source registered for this exact item, or undefined when there is none. */
export function findOneDriveKnowledgeSource(
  sources: readonly KnowledgeSource[],
  address: OneDriveKnowledgeAddress,
): KnowledgeSource | undefined {
  return sources.find(
    (source) =>
      isOneDriveKnowledgeSource(source) &&
      source.config.scope_id === address.scopeId &&
      source.config.item_id === address.itemId,
  );
}

/** Answers the source itself at that address, for a surface that has to act on the source, not just show it. */
export type OneDriveKnowledgeLookup = (
  address: OneDriveKnowledgeAddress,
) => KnowledgeSource | undefined;

/**
 * The workspace's knowledge shelf, read by address, for the rows of a OneDrive surface.
 *
 * The shelf is read only for a caller who could add to it — {@link canAddArtifact}, the same predicate
 * every other knowledge surface gates its own read with. With the read off nothing is found and nothing
 * else changes; `enabled` is the caller's own gate on top of that, so a dialog keeps it off until it
 * opens.
 */
export function useOneDriveKnowledgeLookup({
  workspaceId,
  role,
  enabled = true,
}: {
  workspaceId: string;
  /** The caller's role in this workspace, resolved without platform-admin elevation. */
  role: string | null;
  enabled?: boolean;
}): OneDriveKnowledgeLookup {
  const query = useQuery({
    ...knowledgeSourcesQueryOptions(workspaceId),
    enabled: enabled && canAddArtifact(role),
  });
  const sources = query.data ?? [];
  return (address) => findOneDriveKnowledgeSource(sources, address);
}

/** The confirmation's words: what is about to happen, and what it costs. */
export interface OneDriveKnowledgeConfirmation {
  title: string;
  description: string;
}

/**
 * What the confirmation says before a registered folder is taken back off the shelf. It promises exactly
 * what the action does and no more: the row removes the folder's ROOT source only, while the picker
 * deliberately allows a subfolder or a single file from inside the same folder to be registered on its
 * own. Those survive a root removal, so a sentence saying nobody could search this folder any more was
 * false — and a confirmation that overstates its own reach teaches people to distrust the next one.
 *
 * Two things it does still say, because both are real: the index is thrown away rather than parked, so
 * putting the folder back indexes it from scratch, and the files themselves are untouched. The second
 * earns its place from where the button sits — one place along from Settings and Remove folder in a
 * file-management screen, where the thing a mis-click is feared for is the files.
 */
export function oneDriveKnowledgeRemovalConfirmation(
  name: string,
  isFile = false,
): OneDriveKnowledgeConfirmation {
  return {
    title: `Remove “${name}” from the knowledge base?`,
    description: isFile
      ? 'This source stops answering searches and its index is deleted, so adding the file back indexes it again from scratch. The file stays in your OneDrive.'
      : 'This source stops answering searches and its index is deleted, so adding the folder back indexes it again from scratch. Anything added separately from inside this folder stays searchable. The files stay in your OneDrive.',
  };
}
